import React, { useState, useEffect } from 'react';
import styles from '../GstFetch/GstFetch.module.scss';
import FetchGstModal from '../FetchGstModal/FetchGstModal';

import step_done from '../../../../../assets/step-done.svg';
import step_not_done from '../../../../../assets/step-not-done.svg';
import fetch_purple from '../../../../../assets/fetch-purple.svg';
import { fetchOrangeIcon, GREEN_BG_WHITE_TICK_CIRCLE, gstFetchPurple } from '../../../../../assets/hostedassets';
import clsx from 'clsx';
import GSTModal from './GSTModal/Components/GSTModal';
import { GstContext } from './GSTModal/Context/GstContext';
import { Mixpanel } from '../../../../../utils/mixpanel';
import { COMPANY_GST_FETCH_GST } from '../../../../../utils/constants/mixpanelEvents/companyEvents';
import FetchIcon from '../../../../../assets/Svg/FetchIcon';
import { useSearchParams } from 'react-router-dom';
import GSTUpload from '../GSTUpload/GSTUpload';
import { GST_STATUS } from '../../../../../store/investor/constants';
import { formatDateInIST, DATE_FORMATS } from '../../../../../utils/dateUtils';
import useOptimisticConsent from './useOptimisticConsent';
import { GstMasterListItem } from '../../../../../store/company/types';

export default function GstFetch({
    rawGstData,
    fetchData,
    isInvestor,
    uploadJourneyData,
    gstReportsData,
    updateFunction,
    categoryId,
    flowId,
    assesseeOrgId,
}: any) {
    const [gstInfo, setGstInfo] = useState({
        state: '',
        gstin_number: '',
    });
    const [showModal, setShowModal] = useState(false);
    const [searchParams] = useSearchParams();
    const { pendingConsents, setOptimisticConsent, reconcileOptimisticConsent } = useOptimisticConsent();

    const fetchGst = (gstData: any) => {
        Mixpanel.track(COMPANY_GST_FETCH_GST);
        setGstInfo({
            state: gstData.state ?? '',
            gstin_number: gstData.gstin,
        });
        setShowModal(true);
    };
    const gstData = Array.isArray(rawGstData)
        ? [...rawGstData].sort((a, b) => {
              return a.status === GST_STATUS.INACTIVE ? 1 : -1;
          })
        : [];

    const hasInactiveGst = gstData.some((gst) => gst.status === GST_STATUS.INACTIVE);

    // R6 — reconcile against every refetched gstMasterList so an optimistic "Last consent"
    // value clears once the SNS-driven ConsentAudit row has actually landed server-side.
    useEffect(() => {
        reconcileOptimisticConsent(rawGstData);
    }, [rawGstData, reconcileOptimisticConsent]);

    // R6 — the optimistic value (held until the refetch confirms it) takes precedence over the
    // server's lastConsentAt while pending. The consenter name is never shown on this borrower
    // surface (it moves to the AICA report's audit trail instead).
    // Format in IST, same as the ConsentAuditDrawer and the backend PDF (which pins Asia/Kolkata) —
    // otherwise the same lastConsentAt value prints a different time (and, across midnight, a
    // different date) on this card than on the drawer for any viewer not on IST.
    const consentLineFor = (gst: GstMasterListItem): string | null => {
        const optimistic = pendingConsents[gst.gstin];
        const lastConsentAt = optimistic?.at ?? gst.lastConsentAt;
        if (!lastConsentAt) return null;
        const formatted = formatDateInIST(lastConsentAt, DATE_FORMATS.HISTORY_DRAWER);
        return `Last consent received on ${formatted}`;
    };

    const renderCheck = (check: any) => {
        if (check) return <img src={step_done} alt="" style={{ width: '20px' }} />;
        return <img src={step_not_done} alt="" style={{ width: '20px' }} />;
    };

    return (
        <>
            <GstContext gstValues={{ showModal, setShowModal, gstData: { ...gstInfo, fetchData: fetchData }, setOptimisticConsent }}>
                <div style={isInvestor ? { width: '80%' } : {}} className={styles.GstFetch}>
                    {gstData.map((gst, index) => {
                        const consentLine = consentLineFor(gst);
                        return (
                            <div className={styles.Card} style={isInvestor ? { width: '80%' } : {}} key={gst.gstin}>
                                {index === 0 && (
                                    <>
                                        <div className={styles.CardHeader}>GST Report</div>
                                        <div className={styles.CardSubHeader}>
                                            Fetch reports directly from GST portal (view only access).
                                        </div>
                                    </>
                                )}
                                <div className={styles.GstRow}>
                                    <div className={styles.Place}>
                                        {gst.state && <span style={{ marginRight: '0.5ch' }}>{gst.state}</span>}
                                        {gst.gstin && <span>({gst.gstin})</span>}
                                        {gst.status === GST_STATUS.INACTIVE && <span className={styles.InactivePill}>Inactive</span>}
                                        {gst.status === GST_STATUS.EXPIRED && <span className={styles.ExpiredPill}>Disconnected</span>}
                                    </div>

                                    {gst.status === GST_STATUS.INACTIVE ? (
                                        <div className={styles.DisabledBtn}>
                                            <span>Upload below</span>
                                        </div>
                                    ) : (
                                        <div
                                            className={clsx(styles.FetchBtn, {
                                                [styles.FetchSuccessful]: gst.connected,
                                            })}
                                            onClick={() => {
                                                if (gst.connected) return;
                                                fetchGst(gst);
                                            }}
                                        >
                                            <img
                                                src={gst.connected ? GREEN_BG_WHITE_TICK_CIRCLE : gstFetchPurple}
                                                alt=""
                                                height={14}
                                                width={14}
                                            />
                                            {gst.connected ? 'Fetched' : 'Fetch'}
                                        </div>
                                    )}
                                </div>

                                {consentLine && <span className={styles.LastConsent}>{consentLine}</span>}
                            </div>
                        );
                    })}

                    {/*{searchParams.get('configName') !== 'MAGIC_LINK' && (*/}
                    {/*    <div className={styles.Separator}>*/}
                    {/*        <div className={styles.Line}></div>*/}
                    {/*        <div>Or</div>*/}
                    {/*        <div className={styles.Line}></div>*/}
                    {/*    </div>*/}
                    {/*)}*/}

                    <GSTModal />
                </div>
            </GstContext>

            {searchParams.get('configName') !== 'MAGIC_LINK' && (
                <GSTUpload
                    isInvestor={isInvestor}
                    gstReportsData={gstReportsData}
                    updateFunction={updateFunction}
                    categoryId={categoryId}
                    flowId={flowId}
                    assesseeOrgId={assesseeOrgId}
                    hasInactiveGst={hasInactiveGst}
                />
            )}
        </>
    );
}
